import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate, Link } from 'react-router-dom';
import { Sidebar } from '../../components/Sidebar';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { Checkbox } from '../../components/ui/checkbox';
import { Search, Filter, Sparkles, Instagram, Youtube, MapPin, IndianRupee, Award, Check, X, Send, ListChecks, Plus, ChevronDown, ChevronLeft, FileText, Package, Play, GraduationCap, Star, PartyPopper, Bookmark, Building2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_OPTIONS = ['fashion', 'beauty', 'fitness', 'food', 'travel', 'technology', 'gaming', 'lifestyle', 'entertainment', 'finance', 'parenting', 'education', 'skincare', 'luxury', 'menswear', 'yoga', 'comedy', 'music'];
const CITY_OPTIONS = ['Mumbai', 'Delhi', 'Bangalore', 'Hyderabad', 'Chennai', 'Pune', 'Kolkata', 'Ahmedabad', 'Jaipur', 'Kochi', 'Lucknow'];
const LANGUAGE_OPTIONS = ['Hindi', 'English', 'Tamil', 'Telugu', 'Marathi', 'Punjabi', 'Bengali', 'Gujarati', 'Kannada', 'Malayalam'];
const QUALITY_OPTIONS = ['A+', 'A', 'B', 'C', 'D'];
const PLATFORM_OPTIONS = ['Instagram', 'YouTube', 'Reels', 'Shorts'];

const FOLLOWER_RANGES = [
  { label: 'Nano (1K–10K)', min: 1000, max: 10000 },
  { label: 'Micro (10K–50K)', min: 10000, max: 50000 },
  { label: 'Mid (50K–250K)', min: 50000, max: 250000 },
  { label: 'Macro (250K–1M)', min: 250000, max: 1000000 },
  { label: 'Mega (1M+)', min: 1000000, max: 999999999 },
  { label: 'All ranges', min: null, max: null },
];

const fmtNum = (n) => {
  if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
  if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
  return n;
};

export const BrandDiscover = () => {
  const navigate = useNavigate();
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [shortlists, setShortlists] = useState([]);
  const [filters, setFilters] = useState({
    categories: [],
    cities: [],
    languages: [],
    quality_grades: [],
    gender: '',
    exclusivity_type: 'both',
    platform_filter: 'all',
    follower_range: 'All ranges',
    min_budget: '',
    max_budget: '',
    query: '',
  });
  const [briefOpen, setBriefOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const PAGE_SIZE = 50;

  // Load brand's shortlists once on mount + on demand (e.g. after creating one)
  const loadShortlists = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/shortlists`, { withCredentials: true });
      setShortlists(Array.isArray(data) ? data : []);
    } catch {
      // silent — non-critical
    }
  };
  useEffect(() => { loadShortlists(); }, []);

  // ---------------------------------------------------------------------
  // Feature 3 — Saved Creator Lists (persistent per-brand folders).
  // Fetched once on mount and refreshed after any bookmark-dropdown mutation.
  // ---------------------------------------------------------------------
  const [savedLists, setSavedLists] = useState([]);
  const loadSavedLists = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/brand/saved-lists`, { withCredentials: true });
      setSavedLists(Array.isArray(data) ? data : []);
    } catch {
      // silent — non-critical
    }
  };
  useEffect(() => { loadSavedLists(); }, []);

  // Map of creator_id -> array of shortlist objects the creator is saved into.
  // Powers the "saved" indicator pill on each card.
  const savedMap = React.useMemo(() => {
    const m = {};
    shortlists.forEach((s) => {
      (s.creator_ids || []).forEach((cid) => {
        if (!m[cid]) m[cid] = [];
        m[cid].push(s);
      });
    });
    return m;
  }, [shortlists]);

  const handleSaveToShortlist = async (creatorId, shortlistId) => {
    try {
      await axios.post(
        `${API_URL}/api/shortlists/${shortlistId}/creators`,
        { creator_ids: [creatorId] },
        { withCredentials: true }
      );
      toast.success('Saved to shortlist');
      loadShortlists();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    }
  };

  const handleQuickCreateShortlist = async (creatorId, name) => {
    try {
      const { data } = await axios.post(
        `${API_URL}/api/shortlists`,
        { name, creator_ids: [creatorId] },
        { withCredentials: true }
      );
      toast.success('Shortlist created and creator added');
      loadShortlists();
      return data;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Create failed');
      throw err;
    }
  };

  // Reset to page 1 whenever any filter / query changes — otherwise the brand
  // could be stuck on a page that no longer exists after narrowing results.
  useEffect(() => {
    setPage(1);
  }, [
    filters.categories.join(','),
    filters.cities.join(','),
    filters.languages.join(','),
    filters.quality_grades.join(','),
    filters.gender,
    filters.exclusivity_type,
    filters.platform_filter,
    filters.follower_range,
    filters.min_budget,
    filters.max_budget,
    filters.query,
  ]);

  // Debounced live search whenever any filter OR the active page changes
  useEffect(() => {
    const t = setTimeout(() => { runSearch(); }, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [
    filters.categories.join(','),
    filters.cities.join(','),
    filters.languages.join(','),
    filters.quality_grades.join(','),
    filters.gender,
    filters.exclusivity_type,
    filters.platform_filter,
    filters.follower_range,
    filters.min_budget,
    filters.max_budget,
    filters.query,
    page,
  ]);

  const toggleArr = (key, value) => {
    setFilters((prev) => {
      const arr = prev[key];
      return { ...prev, [key]: arr.includes(value) ? arr.filter((v) => v !== value) : [...arr, value] };
    });
  };

  const toggleShortlist = () => {
    // Kept for legacy compatibility — superseded by per-creator save-to-shortlist
    // dropdown rendered inside CreatorCard. No-op.
  };

  const runSearch = async () => {
    setLoading(true);
    try {
      const range = FOLLOWER_RANGES.find((r) => r.label === filters.follower_range) || FOLLOWER_RANGES[5];
      const payload = {
        categories: filters.categories.length ? filters.categories : null,
        cities: filters.cities.length ? filters.cities : null,
        languages: filters.languages.length ? filters.languages : null,
        quality_grades: filters.quality_grades.length ? filters.quality_grades : null,
        gender: filters.gender || null,
        exclusivity_type: filters.exclusivity_type === 'both' ? null : filters.exclusivity_type,
        platform_filter: filters.platform_filter === 'all' ? null : filters.platform_filter,
        min_followers: range.min,
        max_followers: range.max,
        min_budget: filters.min_budget ? parseFloat(filters.min_budget) : null,
        max_budget: filters.max_budget ? parseFloat(filters.max_budget) : null,
        query: filters.query.trim() || null,
        page,
        page_size: PAGE_SIZE,
      };
      const { data } = await axios.post(`${API_URL}/api/creators/search`, payload, { withCredentials: true });
      // Server returns a paginated envelope: { results, total, page, page_size, total_pages }
      setResults(Array.isArray(data?.results) ? data.results : []);
      setTotal(Number(data?.total || 0));
      setTotalPages(Number(data?.total_pages || 0));
    } catch (err) {
      console.error(err);
      // Silent on errors during live debounced search — the auto-refresh
      // interceptor handles transient 401s. If you still see issues,
      // re-login manually.
    } finally {
      setLoading(false);
    }
  };

  const clearFilters = () => {
    setFilters({
      categories: [],
      cities: [],
      languages: [],
      quality_grades: [],
      gender: '',
      exclusivity_type: 'both',
      platform_filter: 'all',
      follower_range: 'All ranges',
      min_budget: '',
      max_budget: '',
      query: '',
    });
  };

  return (
    <div className="flex min-h-screen">
      <Sidebar />
      <div className="flex-1 p-8 bg-white dark:bg-[#0A0B0D]" data-testid="brand-discover-page">
        <div className="max-w-7xl mx-auto">
          <div className="flex justify-between items-start mb-8 flex-wrap gap-4">
            <div>
              <h1 className="text-4xl font-bold tracking-tight text-[#0F0F0F] dark:text-white mb-2 flex items-center gap-3">
                <Sparkles className="text-[#FF451A]" size={32} />
                AI Creator Discovery
              </h1>
              <p className="text-[#6B6B6B] dark:text-zinc-400">Find the perfect creators with our AI matching engine.</p>
            </div>
            {shortlists.length > 0 ? (
              <Link
                to="/brand/shortlists"
                className="bg-[#FF451A] text-[#0F0F0F] dark:text-white rounded-full px-5 py-2.5 font-medium hover:bg-[#E63E17] transition-colors flex items-center gap-2 shadow-[0_4px_20px_rgba(255,69,26,0.3)]"
                data-testid="open-shortlists-button"
              >
                <ListChecks size={16} /> My Shortlists ({shortlists.length})
              </Link>
            ) : (
              <Link
                to="/brand/shortlists"
                className="border border-black/[0.10] dark:border-white/10 bg-white/5 text-zinc-300 hover:text-white hover:bg-white/10 rounded-full px-5 py-2.5 font-medium transition-colors flex items-center gap-2"
                data-testid="open-shortlists-button"
              >
                <ListChecks size={16} /> Shortlists
              </Link>
            )}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            {/* Sidebar Filters */}
            <aside className="lg:col-span-1 bg-white dark:bg-[#0A0B0D] border border-black/[0.08] dark:border-white/5 rounded-2xl shadow-[0_2px_12px_rgba(0,0,0,0.03)] p-5 space-y-5 h-fit lg:sticky lg:top-6 lg:max-h-[calc(100vh-3rem)] lg:overflow-y-auto">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-[#0F0F0F] dark:text-white flex items-center gap-2"><Filter size={18} /> Filters</h3>
                <button onClick={clearFilters} className="text-xs text-[#FF451A] hover:text-[#E63E17] font-medium" data-testid="clear-filters-button">Clear</button>
              </div>

              <div>
                <label className="text-xs font-semibold uppercase tracking-[0.08em] block mb-2 text-[#0F0F0F] dark:text-white">Search</label>
                <Input
                  value={filters.query}
                  onChange={(e) => setFilters({ ...filters, query: e.target.value })}
                  onKeyDown={(e) => e.key === 'Enter' && runSearch()}
                  placeholder="Name, handle, bio..."
                  className="rounded-xl px-3 py-2 bg-white dark:bg-[#1A1D24] border border-black/[0.12] dark:border-white/[0.08] text-[#0F0F0F] dark:text-white placeholder-[#A1A1AA] dark:placeholder-zinc-500"
                  data-testid="search-input"
                />
              </div>

              <div>
                <label className="text-xs font-semibold uppercase tracking-[0.08em] block mb-2 text-[#0F0F0F] dark:text-white">Follower Range</label>
                <Select value={filters.follower_range} onValueChange={(v) => setFilters({ ...filters, follower_range: v })}>
                  <SelectTrigger className="rounded-xl bg-white dark:bg-[#1A1D24] border border-black/[0.12] dark:border-white/[0.08] text-[#0F0F0F] dark:text-white placeholder-[#A1A1AA] dark:placeholder-zinc-500" data-testid="follower-range-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {FOLLOWER_RANGES.map((r) => (
                      <SelectItem key={r.label} value={r.label}>{r.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="text-xs font-semibold uppercase tracking-[0.08em] block mb-2 text-[#0F0F0F] dark:text-white">Exclusivity</label>
                <Select value={filters.exclusivity_type} onValueChange={(v) => setFilters({ ...filters, exclusivity_type: v })}>
                  <SelectTrigger className="rounded-xl bg-white dark:bg-[#1A1D24] border border-black/[0.12] dark:border-white/[0.08] text-[#0F0F0F] dark:text-white placeholder-[#A1A1AA] dark:placeholder-zinc-500" data-testid="exclusivity-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="both">All Creators</SelectItem>
                    <SelectItem value="exclusive">Exclusive (Managed)</SelectItem>
                    <SelectItem value="marketplace">Marketplace</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="text-xs font-semibold uppercase tracking-[0.08em] block mb-2 text-[#0F0F0F] dark:text-white">Platform</label>
                <div className="flex flex-wrap gap-1.5" data-testid="platform-filter-group">
                  {[
                    { key: 'all',       label: 'All',       icon: null },
                    { key: 'instagram', label: 'Instagram', icon: Instagram, color: '#E1306C' },
                    { key: 'youtube',   label: 'YouTube',   icon: Youtube,   color: '#FF0000' },
                    { key: 'both',      label: 'Both',      icon: null },
                  ].map((opt) => {
                    const active = filters.platform_filter === opt.key;
                    const Icon = opt.icon;
                    return (
                      <button
                        key={opt.key}
                        type="button"
                        onClick={() => setFilters({ ...filters, platform_filter: opt.key })}
                        className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors flex items-center gap-1.5 border ${
                          active
                            ? 'bg-[#FF451A] text-white border-[#FF451A]'
                            : 'bg-white dark:bg-[#0A0B0D] text-[#6B6B6B] dark:text-zinc-400 border-black/[0.10] dark:border-white/10 hover:bg-black/5 dark:hover:bg-white/5'
                        }`}
                        data-testid={`platform-filter-${opt.key}`}
                      >
                        {opt.key === 'both' && (
                          <span className="flex items-center -space-x-1">
                            <Instagram size={12} style={{ color: active ? '#fff' : '#E1306C' }} />
                            <Youtube size={13} style={{ color: active ? '#fff' : '#FF0000' }} />
                          </span>
                        )}
                        {Icon && (
                          <Icon size={13} style={{ color: active ? '#fff' : opt.color }} />
                        )}
                        {opt.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div>
                <label className="text-xs font-semibold uppercase tracking-[0.08em] block mb-2 text-[#0F0F0F] dark:text-white">Categories</label>
                <div className="flex flex-wrap gap-1.5">
                  {CATEGORY_OPTIONS.map((cat) => (
                    <button
                      key={cat}
                      type="button"
                      onClick={() => toggleArr('categories', cat)}
                      className={`px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${
                        filters.categories.includes(cat) ? 'bg-[#FF451A] text-[#0F0F0F] dark:text-white' : 'bg-white dark:bg-[#0A0B0D] text-[#6B6B6B] dark:text-zinc-400 hover:bg-black/10'
                      }`}
                      data-testid={`filter-category-${cat}`}
                    >
                      {cat}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="text-xs font-semibold uppercase tracking-[0.08em] block mb-2 text-[#0F0F0F] dark:text-white">Cities</label>
                <div className="flex flex-wrap gap-1.5">
                  {CITY_OPTIONS.map((city) => (
                    <button
                      key={city}
                      type="button"
                      onClick={() => toggleArr('cities', city)}
                      className={`px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${
                        filters.cities.includes(city) ? 'bg-[#0055FF] text-[#0F0F0F] dark:text-white' : 'bg-white dark:bg-[#0A0B0D] text-[#6B6B6B] dark:text-zinc-400 hover:bg-black/10'
                      }`}
                    >
                      {city}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="text-xs font-semibold uppercase tracking-[0.08em] block mb-2 text-[#0F0F0F] dark:text-white">Languages</label>
                <div className="flex flex-wrap gap-1.5">
                  {LANGUAGE_OPTIONS.map((lang) => (
                    <button
                      key={lang}
                      type="button"
                      onClick={() => toggleArr('languages', lang)}
                      className={`px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${
                        filters.languages.includes(lang) ? 'bg-[#00C972] text-[#0F0F0F] dark:text-white' : 'bg-white dark:bg-[#0A0B0D] text-[#6B6B6B] dark:text-zinc-400 hover:bg-black/10'
                      }`}
                    >
                      {lang}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="text-xs font-semibold uppercase tracking-[0.08em] block mb-2 text-[#0F0F0F] dark:text-white">Quality Grade</label>
                <div className="flex gap-1.5">
                  {QUALITY_OPTIONS.map((q) => (
                    <button
                      key={q}
                      type="button"
                      onClick={() => toggleArr('quality_grades', q)}
                      className={`px-3 py-1 rounded-full text-xs font-semibold transition-colors ${
                        filters.quality_grades.includes(q) ? 'bg-white dark:bg-[#0A0B0D] text-[#0F0F0F] dark:text-white' : 'bg-white dark:bg-[#0A0B0D] text-[#6B6B6B] dark:text-zinc-400 hover:bg-black/10'
                      }`}
                    >
                      Grade {q}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="text-xs font-semibold uppercase tracking-[0.08em] block mb-2 text-[#0F0F0F] dark:text-white">Gender</label>
                <Select value={filters.gender || 'any'} onValueChange={(v) => setFilters({ ...filters, gender: v === 'any' ? '' : v })}>
                  <SelectTrigger className="rounded-xl bg-white dark:bg-[#1A1D24] border border-black/[0.12] dark:border-white/[0.08] text-[#0F0F0F] dark:text-white placeholder-[#A1A1AA] dark:placeholder-zinc-500">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="any">Any</SelectItem>
                    <SelectItem value="female">Female</SelectItem>
                    <SelectItem value="male">Male</SelectItem>
                    <SelectItem value="non-binary">Non-binary</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="text-xs font-semibold uppercase tracking-[0.08em] block mb-2 text-[#0F0F0F] dark:text-white">Pricing/Reel (₹)</label>
                <div className="grid grid-cols-2 gap-2">
                  <Input
                    type="number"
                    value={filters.min_budget}
                    onChange={(e) => setFilters({ ...filters, min_budget: e.target.value })}
                    placeholder="Min"
                    className="rounded-xl bg-white dark:bg-[#1A1D24] border border-black/[0.12] dark:border-white/[0.08] text-[#0F0F0F] dark:text-white placeholder-[#A1A1AA] dark:placeholder-zinc-500"
                    data-testid="min-budget-input"
                  />
                  <Input
                    type="number"
                    value={filters.max_budget}
                    onChange={(e) => setFilters({ ...filters, max_budget: e.target.value })}
                    placeholder="Max"
                    className="rounded-xl bg-white dark:bg-[#1A1D24] border border-black/[0.12] dark:border-white/[0.08] text-[#0F0F0F] dark:text-white placeholder-[#A1A1AA] dark:placeholder-zinc-500"
                    data-testid="max-budget-input"
                  />
                </div>
              </div>

              <Button
                onClick={runSearch}
                disabled={loading}
                className="w-full bg-[#FF451A] text-[#0F0F0F] dark:text-white rounded-xl px-4 py-2.5 font-medium hover:bg-[#E63E17] transition-colors"
                data-testid="apply-filters-button"
              >
                <Search size={16} className="mr-2" />
                {loading ? 'Searching...' : 'Refresh results'}
              </Button>
            </aside>

            {/* Results */}
            <main className="lg:col-span-3">
              <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                <div className="text-sm text-[#6B6B6B] dark:text-zinc-400">
                  <span className="font-semibold text-[#0F0F0F] dark:text-white" data-testid="results-count">{total.toLocaleString()}</span> creators matched
                  {total > 0 && (
                    <span className="text-[#6B6B6B] dark:text-zinc-500 ml-2 font-mono text-xs">
                      · showing {((page - 1) * PAGE_SIZE) + 1}–{Math.min(page * PAGE_SIZE, total)}
                    </span>
                  )}
                  {shortlists.length > 0 && <span className="ml-3 text-[#FF451A]">• {shortlists.length} shortlist{shortlists.length === 1 ? '' : 's'} created</span>}
                </div>
              </div>

              {loading ? (
                <div className="text-center py-20">
                  <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-[#FF451A] border-r-transparent"></div>
                </div>
              ) : results.length === 0 ? (
                <div className="bg-white dark:bg-[#0A0B0D] border border-black/[0.08] dark:border-white/5 rounded-2xl p-12 text-center">
                  <Search className="mx-auto text-[#6B6B6B] dark:text-zinc-500 mb-3" size={40} />
                  <h3 className="text-lg font-semibold text-[#0F0F0F] dark:text-white mb-1">No creators match your filters</h3>
                  <p className="text-sm text-[#6B6B6B] dark:text-zinc-400 mb-4">Try removing some filters or broaden your search.</p>
                  <button
                    onClick={clearFilters}
                    className="text-sm text-[#FF451A] hover:text-[#E63E17] font-medium"
                    data-testid="empty-clear-button"
                  >
                    Clear all filters →
                  </button>
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                    {results.map((c) => {
                      const savedIn = savedMap[c.id] || [];
                      return (
                        <CreatorCard
                          key={c.id}
                          creator={c}
                          savedIn={savedIn}
                          shortlists={shortlists}
                          savedLists={savedLists}
                          onSavedListsChanged={loadSavedLists}
                          onOpenProfile={() => navigate(`/brand/creators/${c.id}`)}
                          onSave={(slId) => handleSaveToShortlist(c.id, slId)}
                          onQuickCreate={(name) => handleQuickCreateShortlist(c.id, name)}
                        />
                      );
                    })}
                  </div>

                  {/* Pagination footer */}
                  {totalPages > 1 && (
                    <div
                      className="mt-6 flex items-center justify-between gap-4 p-4 bg-white dark:bg-[#0A0B0D] border border-black/[0.08] dark:border-white/5 rounded-xl flex-wrap"
                      data-testid="discovery-pagination"
                    >
                      <div className="text-xs text-[#6B6B6B] dark:text-zinc-400 font-mono">
                        Page <span className="text-[#0F0F0F] dark:text-white font-semibold">{page}</span> of {totalPages}
                        <span className="text-[#A1A1AA] dark:text-zinc-600 ml-2">· {total.toLocaleString()} creators</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          onClick={() => setPage(1)}
                          disabled={page <= 1 || loading}
                          className="text-xs px-3 py-1.5 rounded-full bg-white/5 border border-black/[0.10] dark:border-white/10 text-zinc-300 hover:text-white hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed"
                          data-testid="pagination-first"
                        >
                          « First
                        </Button>
                        <Button
                          onClick={() => setPage((p) => Math.max(1, p - 1))}
                          disabled={page <= 1 || loading}
                          className="text-xs px-3 py-1.5 rounded-full bg-white/5 border border-black/[0.10] dark:border-white/10 text-zinc-300 hover:text-white hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed"
                          data-testid="pagination-prev"
                        >
                          ‹ Prev
                        </Button>
                        <Button
                          onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                          disabled={page >= totalPages || loading}
                          className="text-xs px-3 py-1.5 rounded-full bg-[#FF451A] text-[#0F0F0F] dark:text-white hover:bg-[#E63E17] disabled:opacity-30 disabled:cursor-not-allowed"
                          data-testid="pagination-next"
                        >
                          Next ›
                        </Button>
                        <Button
                          onClick={() => setPage(totalPages)}
                          disabled={page >= totalPages || loading}
                          className="text-xs px-3 py-1.5 rounded-full bg-white/5 border border-black/[0.10] dark:border-white/10 text-zinc-300 hover:text-white hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed"
                          data-testid="pagination-last"
                        >
                          Last »
                        </Button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </main>
          </div>

          {/* Brief Dialog (legacy — kept for compatibility but unused now;
              briefs are created from a shortlist's workspace) */}
          <BriefDialog
            open={briefOpen}
            onClose={() => setBriefOpen(false)}
            shortlist={[]}
            onSent={() => setBriefOpen(false)}
          />
        </div>
      </div>
    </div>
  );
};

const CreatorCard = ({ creator, savedIn = [], shortlists = [], savedLists = [], onSavedListsChanged, onOpenProfile, onSave, onQuickCreate }) => {
  const matchColor = (creator.match_score || 0) >= 80 ? '#00C972' : (creator.match_score || 0) >= 60 ? '#FFB800' : '#999999';
  const isSaved = savedIn.length > 0;
  const [menuOpen, setMenuOpen] = useState(false);
  const [quickName, setQuickName] = useState('');
  const [showQuickCreate, setShowQuickCreate] = useState(false);
  const [quickError, setQuickError] = useState('');
  const [creating, setCreating] = useState(false);
  // Feature 3 — Saved List bookmark dropdown state (independent of shortlist menu)
  const [bookmarkOpen, setBookmarkOpen] = useState(false);
  const [newListName, setNewListName] = useState('');
  const [showNewListForm, setShowNewListForm] = useState(false);
  const bookmarkedIn = React.useMemo(
    () => savedLists.filter((l) => (l.creator_ids || []).includes(creator.id)),
    [savedLists, creator.id]
  );
  const isBookmarked = bookmarkedIn.length > 0;

  // Any popover open on this card → the card must float above cards below it
  // (iPad Safari does not honour our z-50 alone because sibling cards create
  // their own stacking contexts). Elevating the card wrapper solves this.
  const anyMenuOpen = menuOpen || bookmarkOpen;

  const submitQuickCreate = async () => {
    const nm = quickName.trim();
    if (!nm) {
      setQuickError('Please enter a name');
      return;
    }
    setQuickError('');
    setCreating(true);
    try {
      await onQuickCreate(nm);
      setQuickName('');
      setShowQuickCreate(false);
      setMenuOpen(false);
    } catch (_e) {
      // toast handled upstream
    } finally {
      setCreating(false);
    }
  };

  const toggleInList = async (list) => {
    const inThis = (list.creator_ids || []).includes(creator.id);
    try {
      if (inThis) {
        await axios.delete(`${API_URL}/api/brand/saved-lists/${list.list_id}/creators/${creator.id}`, { withCredentials: true });
        toast.success(`Removed from "${list.name}"`);
      } else {
        await axios.post(`${API_URL}/api/brand/saved-lists/${list.list_id}/add-creator`, { creator_id: creator.id }, { withCredentials: true });
        toast.success(`Added to "${list.name}"`);
      }
      onSavedListsChanged && onSavedListsChanged();
    } catch {
      toast.error('Could not update list');
    }
  };

  const createAndAdd = async () => {
    const nm = newListName.trim();
    if (!nm) return;
    try {
      await axios.post(
        `${API_URL}/api/brand/saved-lists`,
        { name: nm, creator_ids: [creator.id] },
        { withCredentials: true }
      );
      toast.success(`Added to new list "${nm}"`);
      setNewListName('');
      setBookmarkOpen(false);
      onSavedListsChanged && onSavedListsChanged();
    } catch {
      toast.error('Could not create list');
    }
  };

  // Click on the card body opens the profile; the action zone at the bottom
  // (save dropdown) is excluded via stopPropagation.
  return (
    <div
      onClick={onOpenProfile}
      className={`bg-white dark:bg-[#0A0B0D] rounded-2xl shadow-[0_2px_12px_rgba(0,0,0,0.03)] p-5 transition-all duration-200 hover:-translate-y-[2px] cursor-pointer relative ${
        anyMenuOpen ? 'z-[100]' : 'z-10'
      } ${
        isSaved ? 'border-2 border-[#00C972]/40' : 'border border-black/[0.08] dark:border-white/5 hover:border-white/15'
      }`}
      data-testid="creator-card"
    >
      {/* Feature 3 — Saved List bookmark, top-right corner */}
      <div
        onClick={(e) => e.stopPropagation()}
        className={`absolute top-3 right-3 ${bookmarkOpen ? 'z-[110]' : 'z-10'}`}
        data-testid="card-bookmark-zone"
      >
        <button
          type="button"
          onClick={() => setBookmarkOpen((v) => !v)}
          className={`w-8 h-8 rounded-full flex items-center justify-center border transition-colors ${
            isBookmarked
              ? 'bg-[#FF451A] border-[#FF451A] text-white hover:bg-[#E63E17]'
              : 'bg-white dark:bg-[#0A0B0D] border-black/[0.10] dark:border-white/10 text-[#6B6B6B] dark:text-zinc-400 hover:text-[#FF451A] hover:border-[#FF451A]/40'
          }`}
          data-testid={isBookmarked ? 'card-bookmarked-button' : 'card-bookmark-button'}
          title={isBookmarked ? `Saved in ${bookmarkedIn.length} list(s)` : 'Save to a list'}
        >
          <Bookmark size={14} fill={isBookmarked ? 'currentColor' : 'none'} />
        </button>
        {bookmarkOpen && (
          <div
            className="absolute right-0 top-full mt-1.5 w-64 bg-white dark:bg-[#131518] border border-black/[0.10] dark:border-white/10 rounded-xl shadow-2xl p-2 z-[110]"
            data-testid="card-bookmark-menu"
            onClick={(e) => e.stopPropagation()}
          >
            {savedLists.length === 0 ? (
              <div className="px-3 py-2 text-xs text-[#6B6B6B] dark:text-zinc-400">No saved lists yet. Create your first:</div>
            ) : (
              <>
                <div className="px-3 py-1 text-[10px] uppercase tracking-wider text-[#6B6B6B] dark:text-zinc-500 font-mono">Your saved lists</div>
                <div className="max-h-40 overflow-y-auto">
                  {savedLists.map((l) => {
                    const inThis = (l.creator_ids || []).includes(creator.id);
                    return (
                      <button
                        key={l.list_id}
                        type="button"
                        onClick={() => toggleInList(l)}
                        className="w-full text-left px-3 py-2 rounded-lg text-sm flex items-center gap-2 hover:bg-black/[0.03] dark:hover:bg-white/5"
                        data-testid={`card-bookmark-option-${l.list_id}`}
                      >
                        <span className={`w-4 h-4 rounded border flex items-center justify-center ${inThis ? 'bg-[#FF451A] border-[#FF451A]' : 'bg-transparent border-black/[0.20] dark:border-white/20'}`}>
                          {inThis && <Check size={11} className="text-white" />}
                        </span>
                        <span className="flex-1 truncate text-[#0F0F0F] dark:text-white">{l.name}</span>
                        <span className="text-[10px] text-[#6B6B6B] dark:text-zinc-500">{l.creator_count}</span>
                      </button>
                    );
                  })}
                </div>
              </>
            )}
            <div className="border-t border-black/[0.08] dark:border-white/5 mt-2 pt-2 px-2">
              {!showNewListForm ? (
                <button
                  type="button"
                  onClick={() => setShowNewListForm(true)}
                  className="w-full text-left px-2 py-2 rounded-lg text-sm text-[#FF451A] hover:bg-[#FF451A]/10 flex items-center gap-1.5 font-medium transition-colors"
                  data-testid="card-bookmark-new-toggle"
                >
                  <Plus size={14} /> Create new list
                </button>
              ) : (
                <>
                  <div className="text-[10px] uppercase tracking-wider text-[#6B6B6B] dark:text-zinc-500 font-mono mb-1.5">New list name</div>
                  <div className="flex gap-1.5">
                    <Input
                      value={newListName}
                      onChange={(e) => setNewListName(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') createAndAdd(); }}
                      autoFocus
                      placeholder="e.g. Favourites"
                      className="rounded-lg text-xs h-8 bg-white dark:bg-[#1A1D24] border border-black/[0.12] dark:border-white/[0.08] text-[#0F0F0F] dark:text-white placeholder-[#A1A1AA] dark:placeholder-zinc-500"
                      data-testid="card-bookmark-new-input"
                    />
                    <Button
                      onClick={createAndAdd}
                      className="bg-[#FF451A] text-white rounded-lg px-2.5 h-8 hover:bg-[#E63E17] text-xs"
                      data-testid="card-bookmark-new-button"
                    >
                      Create
                    </Button>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </div>
      <div className="flex items-start gap-3 mb-3">
        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#FF451A] to-[#0055FF] flex items-center justify-center text-[#0F0F0F] dark:text-white text-lg font-bold shrink-0">
          {creator.name?.[0]?.toUpperCase() || 'C'}
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-[#0F0F0F] dark:text-white truncate" data-testid="creator-name">{creator.name}</h3>
          <div className="flex items-center gap-2 text-xs text-[#6B6B6B] dark:text-zinc-400">
            {creator.platform_presence === 'yt_only' ? (
              <span className="flex items-center gap-1 text-[#FF0000]" data-testid="platform-yt-only-label">
                <Youtube size={13} /> YouTube Creator
              </span>
            ) : (
              <>
                {creator.instagram_handle && (
                  <span className="flex items-center gap-1" data-testid="platform-ig">
                    <Instagram size={12} /> @{creator.instagram_handle}
                  </span>
                )}
                {creator.platform_presence === 'ig_and_yt' && (
                  <span className="flex items-center gap-1 text-[#FF0000]" title="Also on YouTube" data-testid="platform-yt">
                    <Youtube size={13} />
                    {creator.youtube_subscribers > 0 && (
                      <span className="text-[10px] font-mono">{(creator.youtube_subscribers / 1000).toFixed(0)}K</span>
                    )}
                  </span>
                )}
              </>
            )}
          </div>
        </div>
        {creator.match_score != null && (
          <div className="text-right">
            <div className="text-xs text-[#6B6B6B] dark:text-zinc-400 mb-0.5">Match</div>
            <div className="text-lg font-bold" style={{ color: matchColor }} data-testid="match-score">
              {creator.match_score}
            </div>
          </div>
        )}
      </div>

      {creator.bio && <p className="text-xs text-[#6B6B6B] dark:text-zinc-400 mb-3 line-clamp-2">{creator.bio}</p>}

      {/* Exclusivity + Quality Grade chips */}
      <div className="flex flex-wrap items-center gap-1.5 mb-3 min-h-[22px]">
        {(() => {
          const badge = creator.exclusivity_badge;
          const status = creator.exclusivity_status;
          if (!badge && !status) return null;
          const label = badge?.label || (status ? status.replace('_', ' ').replace(/\b\w/g, (c) => c.toUpperCase()) : null);
          if (!label) return null;
          // Map badge/status color → concrete Tailwind arbitrary values with proper contrast in both themes.
          const colorKey = (badge?.color || {
            exclusive: 'green', trial: 'orange', brand_exclusive: 'blue', prospect: 'grey',
          }[status] || 'grey').toLowerCase();
          const classes = {
            green:  'bg-[#00C972]/15 text-[#00C972] border-[#00C972]/30',
            orange: 'bg-[#FF9500]/15 text-[#FF9500] border-[#FF9500]/30',
            blue:   'bg-[#0055FF]/15 text-[#0055FF] border-[#0055FF]/30',
            grey:   'bg-white/5 text-[#6B6B6B] dark:text-zinc-400 border-black/[0.10] dark:border-white/10',
          }[colorKey] || 'bg-white/5 text-[#6B6B6B] dark:text-zinc-400 border-black/[0.10] dark:border-white/10';
          return (
            <span
              className={`px-2 py-0.5 text-[10px] uppercase tracking-wider font-semibold rounded-full border ${classes}`}
              data-testid="card-exclusivity-badge"
            >
              {label}
            </span>
          );
        })()}
        {creator.quality_grade && (
          <span
            className="px-2 py-0.5 text-[10px] uppercase tracking-wider font-semibold rounded-full border bg-[#FFB800]/10 text-[#B8860B] dark:text-[#FFB800] border-[#FFB800]/30 inline-flex items-center gap-1"
            data-testid="card-quality-grade"
          >
            <Award size={9} /> {creator.quality_grade}
          </span>
        )}
      </div>

      {isSaved && (
        <div className="mb-2 inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-[#00C972] bg-[#00C972]/10 border border-[#00C972]/20 px-2 py-0.5 rounded-full font-mono" data-testid="card-saved-indicator">
          <Check size={10} /> Saved in {savedIn.length} shortlist{savedIn.length === 1 ? '' : 's'}
        </div>
      )}

      <div className="grid grid-cols-2 gap-2 text-xs mb-3">
        {creator.platform_presence === 'yt_only' ? (
          <>
            <div className="bg-white/[0.03] border border-black/[0.08] dark:border-white/5 rounded-lg px-2 py-1.5" data-testid="stat-yt-subs-card">
              <div className="text-[#6B6B6B] dark:text-zinc-500 text-[10px] uppercase">YT Subscribers</div>
              <div className="font-semibold text-[#0F0F0F] dark:text-white flex items-center gap-1">
                <Youtube size={11} className="text-[#FF0000]" />
                {fmtNum(creator.youtube_subscribers)}
              </div>
            </div>
            <div className="bg-white/[0.03] border border-black/[0.08] dark:border-white/5 rounded-lg px-2 py-1.5">
              <div className="text-[#6B6B6B] dark:text-zinc-500 text-[10px] uppercase">Short Price</div>
              <div className="font-semibold text-[#0F0F0F] dark:text-white flex items-center gap-0.5">
                <IndianRupee size={11} />
                {fmtNum(creator.youtube_pricing?.short || 0)}
              </div>
            </div>
            <div className="bg-white/[0.03] border border-black/[0.08] dark:border-white/5 rounded-lg px-2 py-1.5">
              <div className="text-[#6B6B6B] dark:text-zinc-500 text-[10px] uppercase">Integrated</div>
              <div className="font-semibold text-[#0F0F0F] dark:text-white flex items-center gap-0.5">
                <IndianRupee size={11} />
                {fmtNum(creator.youtube_pricing?.integrated || 0)}
              </div>
            </div>
            <div className="bg-white/[0.03] border border-black/[0.08] dark:border-white/5 rounded-lg px-2 py-1.5">
              <div className="text-[#6B6B6B] dark:text-zinc-500 text-[10px] uppercase">Dedicated</div>
              <div className="font-semibold text-[#0F0F0F] dark:text-white flex items-center gap-0.5">
                <IndianRupee size={11} />
                {fmtNum(creator.youtube_pricing?.dedicated || 0)}
              </div>
            </div>
          </>
        ) : (
          <>
            <div className="bg-white/[0.03] border border-black/[0.08] dark:border-white/5 rounded-lg px-2 py-1.5">
              <div className="text-[#6B6B6B] dark:text-zinc-500 text-[10px] uppercase">IG Followers</div>
              <div className="font-semibold text-[#0F0F0F] dark:text-white flex items-center gap-1">
                <Instagram size={11} className="text-[#E1306C]" />
                {fmtNum(creator.instagram_followers)}
              </div>
            </div>
            <div className="bg-white/[0.03] border border-black/[0.08] dark:border-white/5 rounded-lg px-2 py-1.5">
              <div className="text-[#6B6B6B] dark:text-zinc-500 text-[10px] uppercase">YT Subs</div>
              <div className="font-semibold text-[#0F0F0F] dark:text-white flex items-center gap-1">
                <Youtube size={11} className="text-[#FF0000]" />
                {fmtNum(creator.youtube_subscribers)}
              </div>
            </div>
            <div className="bg-white/[0.03] border border-black/[0.08] dark:border-white/5 rounded-lg px-2 py-1.5">
              <div className="text-[#6B6B6B] dark:text-zinc-500 text-[10px] uppercase">Engagement</div>
              <div className="font-semibold text-[#0F0F0F] dark:text-white">{(creator.instagram_engagement || 0).toFixed(1)}%</div>
            </div>
            <div className="bg-white/[0.03] border border-black/[0.08] dark:border-white/5 rounded-lg px-2 py-1.5">
              <div className="text-[#6B6B6B] dark:text-zinc-500 text-[10px] uppercase">Reel Price</div>
              <div className="font-semibold text-[#0F0F0F] dark:text-white flex items-center gap-0.5">
                <IndianRupee size={11} />
                {fmtNum(creator.pricing_reel || 0)}
              </div>
            </div>
          </>
        )}
      </div>

      <div className="flex flex-wrap gap-1.5 mb-3 min-h-[24px]">
        {(creator.categories || []).slice(0, 3).map((cat) => (
          <span key={cat} className="px-2 py-0.5 text-[10px] rounded-full bg-[#FF451A]/10 text-[#FF451A] font-medium">
            {cat}
          </span>
        ))}
        {creator.cities?.[0] && (
          <span className="px-2 py-0.5 text-[10px] rounded-full bg-white dark:bg-[#0A0B0D] text-[#6B6B6B] dark:text-zinc-400 font-medium flex items-center gap-1">
            <MapPin size={9} /> {creator.cities[0]}
          </span>
        )}
        <span
          className={`px-2 py-0.5 text-[10px] rounded-full font-semibold flex items-center gap-1 ${
            creator.exclusivity_type === 'exclusive' ? 'bg-white dark:bg-[#0A0B0D] text-[#0F0F0F] dark:text-white' : 'bg-white dark:bg-[#0A0B0D] text-[#6B6B6B] dark:text-zinc-400'
          }`}
        >
          {creator.exclusivity_type === 'exclusive' ? <Award size={9} /> : null}
          {creator.exclusivity_type === 'exclusive' ? 'Managed' : 'Marketplace'}
        </span>
        {creator.availability_status && (() => {
          const AV = {
            available: { label: 'Available', color: '#00C972', bg: 'rgba(0,201,114,0.15)', border: 'rgba(0,201,114,0.3)' },
            booked: { label: 'Booked', color: '#FF451A', bg: 'rgba(255,69,26,0.15)', border: 'rgba(255,69,26,0.3)' },
            vacation: { label: 'Vacation', color: '#FFB800', bg: 'rgba(255,184,0,0.15)', border: 'rgba(255,184,0,0.3)' },
            paused: { label: 'Paused', color: '#999999', bg: 'rgba(255,255,255,0.05)', border: 'rgba(255,255,255,0.1)' },
            locked: { label: 'Locked', color: '#9B6EFF', bg: 'rgba(155,110,255,0.15)', border: 'rgba(155,110,255,0.3)' },
          }[creator.availability_status];
          if (!AV) return null;
          return (
            <span className="px-2 py-0.5 text-[10px] rounded-full font-mono uppercase tracking-wider border" style={{ backgroundColor: AV.bg, color: AV.color, borderColor: AV.border }} data-testid="creator-availability-badge">
              {AV.label}
            </span>
          );
        })()}
      </div>

      <div onClick={(e) => e.stopPropagation()} className={`relative ${menuOpen ? 'z-[110]' : ''}`} data-testid="card-save-zone">
        <Button
          onClick={() => setMenuOpen((v) => !v)}
          className={`w-full rounded-full px-4 py-2 font-medium text-sm transition-colors flex items-center justify-center gap-1.5 ${
            isSaved
              ? 'bg-[#00C972]/10 border border-[#00C972]/30 text-[#00C972] hover:bg-[#00C972]/15'
              : 'bg-[#FF451A] text-[#0F0F0F] dark:text-white hover:bg-[#E63E17]'
          }`}
          data-testid={isSaved ? 'card-saved-button' : 'card-save-button'}
        >
          {isSaved ? <><Check size={14} /> Saved</> : <><Plus size={14} /> Save to shortlist</>}
          <ChevronDown size={12} />
        </Button>
        {menuOpen && (
          <div className="absolute left-0 right-0 top-full mt-1.5 bg-white dark:bg-[#131518] border border-black/[0.10] dark:border-white/10 rounded-xl shadow-2xl p-2 z-[110]" data-testid="card-save-menu" onClick={(e) => e.stopPropagation()}>
            {shortlists.length === 0 ? (
              <div className="px-3 py-2 text-xs text-[#6B6B6B] dark:text-zinc-400">No shortlists yet. Create your first below.</div>
            ) : (
              <>
                <div className="px-3 py-1 text-[10px] uppercase tracking-wider text-[#6B6B6B] dark:text-zinc-500 font-mono">Your shortlists</div>
                <div className="max-h-40 overflow-y-auto">
                  {shortlists.map((s) => {
                    const already = savedIn.some((x) => x.id === s.id);
                    return (
                      <button
                        key={s.id}
                        type="button"
                        onClick={async () => { if (!already) await onSave(s.id); }}
                        className={`w-full text-left px-3 py-2 rounded-lg text-sm flex items-center gap-2 transition-colors ${
                          already ? 'text-[#6B6B6B] dark:text-zinc-500 cursor-default' : 'text-[#0F0F0F] dark:text-white hover:bg-black/[0.03] dark:hover:bg-white/5'
                        }`}
                        data-testid={`card-save-option-${s.id}`}
                      >
                        <span className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 ${already ? 'bg-[#FF451A] border-[#FF451A]' : 'bg-transparent border-black/[0.20] dark:border-white/20'}`}>
                          {already && <Check size={11} className="text-white" />}
                        </span>
                        <span className="flex-1 truncate">{s.name}</span>
                        <span className="text-[10px] text-[#6B6B6B] dark:text-zinc-500 shrink-0">{s.creator_count}</span>
                      </button>
                    );
                  })}
                </div>
              </>
            )}
            <div className="border-t border-black/[0.08] dark:border-white/5 mt-2 pt-2 px-2">
              {!showQuickCreate ? (
                <button
                  type="button"
                  onClick={() => { setShowQuickCreate(true); setQuickError(''); }}
                  className="w-full text-left px-2 py-2 rounded-lg text-sm text-[#FF451A] hover:bg-[#FF451A]/10 flex items-center gap-1.5 font-medium transition-colors"
                  data-testid="card-quick-create-toggle"
                >
                  <Plus size={14} /> Create new shortlist
                </button>
              ) : (
                <form
                  onSubmit={(e) => { e.preventDefault(); e.stopPropagation(); submitQuickCreate(); }}
                  data-testid="card-quick-create-form"
                >
                  <div className="text-[10px] uppercase tracking-wider text-[#6B6B6B] dark:text-zinc-500 font-mono mb-1.5">New shortlist name</div>
                  <div className="flex gap-1.5">
                    <Input
                      value={quickName}
                      onChange={(e) => { setQuickName(e.target.value); if (quickError) setQuickError(''); }}
                      onClick={(e) => e.stopPropagation()}
                      autoFocus
                      placeholder="e.g. Summer campaign"
                      className={`bg-white dark:bg-[#1A1D24] border rounded-lg text-xs text-[#0F0F0F] dark:text-white placeholder-[#A1A1AA] dark:placeholder-zinc-500 h-8 ${
                        quickError ? 'border-[#FF3B30]' : 'border-black/[0.12] dark:border-white/[0.08]'
                      }`}
                      data-testid="card-quick-create-input"
                      disabled={creating}
                    />
                    <Button
                      type="submit"
                      disabled={creating}
                      className="bg-[#FF451A] text-white rounded-lg px-2.5 h-8 hover:bg-[#E63E17] text-xs disabled:opacity-50"
                      data-testid="card-quick-create-button"
                    >
                      {creating ? '…' : 'Create'}
                    </Button>
                  </div>
                  {quickError && (
                    <div className="text-[11px] text-[#FF3B30] mt-1.5 px-0.5" data-testid="card-quick-create-error">
                      {quickError}
                    </div>
                  )}
                </form>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const BriefDialog = ({ open, onClose, shortlist, onSent }) => {
  const { user } = useAuth();
  const isAgency = user?.role === 'agency';
  const [agencyClients, setAgencyClients] = useState([]);
  const [clientBrandId, setClientBrandId] = useState('');
  const [clientsLoading, setClientsLoading] = useState(false);
  // Feature 1 — Brief Templates.  Show a picker BEFORE the form so brands
  // can start from a pre-built brief.  `showTemplates` gates the picker view;
  // `selectedTemplateId` is stamped on the final submit payload for analytics.
  const [showTemplates, setShowTemplates] = useState(true);
  const [templates, setTemplates] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState(null);

  const [form, setForm] = useState({
    campaign_name: '',
    brief: '',
    platforms: [],
    creator_count: 1,
    followers_range: 'Mid (50K–250K)',
    categories: [],
    languages: ['Hindi'],
    gender: '',
    locations: [],
    budget: '',
    deliverables: [],
    quality_grades: ['A', 'B'],
    exclusive_only: false,
    timeline_start: '',
    timeline_end: '',
    campaign_type: 'exclusive', // 'exclusive' | 'bulk' — Part 2 § 1
    expected_creator_count: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [deliverableInput, setDeliverableInput] = useState('');

  // Fetch templates the first time the dialog opens.
  useEffect(() => {
    if (!open) return;
    setForm((f) => ({ ...f, creator_count: shortlist.length || 1 }));
    if (templates.length === 0 && !templatesLoading) {
      setTemplatesLoading(true);
      axios.get(`${API_URL}/api/brand/brief-templates`, { withCredentials: true })
        .then((r) => setTemplates(r.data || []))
        .catch(() => setTemplates([]))
        .finally(() => setTemplatesLoading(false));
    }
    // Agency users must pick a client brand before submitting on their behalf.
    if (isAgency && agencyClients.length === 0 && !clientsLoading) {
      setClientsLoading(true);
      axios.get(`${API_URL}/api/agency/clients`, { withCredentials: true })
        .then((r) => setAgencyClients(r.data?.clients || []))
        .catch(() => setAgencyClients([]))
        .finally(() => setClientsLoading(false));
    }
  }, [open, shortlist.length, templates.length, templatesLoading, isAgency, agencyClients.length, clientsLoading]);

  // Reset picker state each time the dialog closes so the next open starts clean.
  useEffect(() => {
    if (!open) {
      setShowTemplates(true);
      setSelectedTemplateId(null);
    }
  }, [open]);

  const applyTemplate = (tpl) => {
    // Pre-fill only the fields the template owns.  Brand can still edit any
    // field before submitting.  Platform: templates use 'Instagram' | 'YouTube'
    // | 'both' — map 'both' onto Instagram + YouTube; otherwise single.
    const platforms = tpl.platform === 'both'
      ? ['Instagram', 'YouTube']
      : tpl.platform
        ? [tpl.platform]
        : [];
    setForm((prev) => ({
      ...prev,
      brief: tpl.sample_brief_text || prev.brief,
      creator_count: tpl.suggested_creator_count || prev.creator_count,
      platforms: platforms.length ? platforms : prev.platforms,
      categories: tpl.category_suggestions?.length ? tpl.category_suggestions : prev.categories,
      deliverables: tpl.default_deliverables?.length ? [...tpl.default_deliverables] : prev.deliverables,
    }));
    setSelectedTemplateId(tpl.template_id);
    setShowTemplates(false);
  };

  const skipTemplates = () => {
    // Reset only the fields applyTemplate can mutate so 'Start from scratch'
    // truly opens a blank form even if the user first picked a template.
    setForm((prev) => ({
      ...prev,
      brief: '',
      platforms: [],
      deliverables: [],
      categories: [],
    }));
    setSelectedTemplateId(null);
    setShowTemplates(false);
  };

  // Icon per template_id so the picker cards feel distinct.
  const TEMPLATE_ICONS = {
    product_seeding: Package,
    awareness_reel: Play,
    tutorial_how_to: GraduationCap,
    review_campaign: Star,
    festival_seasonal: PartyPopper,
  };

  const toggleArr = (key, value) => {
    setForm((prev) => {
      const arr = prev[key];
      return { ...prev, [key]: arr.includes(value) ? arr.filter((v) => v !== value) : [...arr, value] };
    });
  };

  const addDeliverable = () => {
    if (!deliverableInput.trim()) return;
    setForm((f) => ({ ...f, deliverables: [...f.deliverables, deliverableInput.trim()] }));
    setDeliverableInput('');
  };

  const submit = async () => {
    if (!form.campaign_name.trim() || !form.brief.trim() || !form.budget) {
      toast.error('Please fill campaign name, brief, and budget');
      return;
    }
    if (isAgency && !clientBrandId) {
      toast.error('Please select the client brand this brief is for');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(
        `${API_URL}/api/brand/campaigns/submit`,
        {
          campaign_name: form.campaign_name,
          brief: form.brief,
          platforms: form.platforms.length ? form.platforms : ['Instagram'],
          creator_count: parseInt(form.creator_count, 10),
          followers_range: form.followers_range,
          categories: form.categories,
          languages: form.languages,
          gender: form.gender || null,
          locations: form.locations,
          budget: parseFloat(form.budget),
          deliverables: form.deliverables.length ? form.deliverables : ['1 Reel + 2 Stories'],
          quality_grades: form.quality_grades,
          exclusive_only: form.exclusive_only,
          timeline_start: form.timeline_start || null,
          timeline_end: form.timeline_end || null,
          shortlisted_creator_ids: shortlist.map((c) => c.id),
          campaign_type: form.campaign_type,
          expected_creator_count: form.campaign_type === 'bulk' && form.expected_creator_count
            ? parseInt(form.expected_creator_count, 10) : null,
          template_id: selectedTemplateId,
          client_brand_id: isAgency ? clientBrandId : undefined,
        },
        { withCredentials: true }
      );
      toast.success('Campaign brief submitted to StrawHat HQ for review!');
      onSent();
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.detail || 'Failed to submit brief');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="brief-dialog">
        <DialogHeader>
          <DialogTitle className="text-2xl">
            {showTemplates ? 'Start from a Template' : 'Submit Campaign Brief'}
          </DialogTitle>
          <DialogDescription className="text-sm text-[#6B6B6B] dark:text-zinc-400">
            {showTemplates
              ? 'Pick a pre-built playbook to pre-fill the brief, or start from scratch.'
              : `${shortlist.length} creator(s) shortlisted. Our team will review and assign managers within 24 hours.`}
          </DialogDescription>
        </DialogHeader>

        {showTemplates ? (
          // ---------------------------------------------------------------
          // Feature 1 — Template picker view
          // ---------------------------------------------------------------
          <div className="mt-4 space-y-3" data-testid="brief-template-picker">
            {templatesLoading && (
              <div className="text-sm text-[#6B6B6B] dark:text-zinc-400 py-6 text-center">
                Loading templates…
              </div>
            )}

            {!templatesLoading && templates.map((tpl) => {
              const Icon = TEMPLATE_ICONS[tpl.template_id] || FileText;
              return (
                <div
                  key={tpl.template_id}
                  className="border border-black/[0.08] dark:border-white/10 rounded-2xl p-4 bg-white dark:bg-[#0F1011] hover:border-[#FF451A]/40 transition-colors"
                  data-testid={`brief-template-${tpl.template_id}`}
                >
                  <div className="flex items-start gap-4">
                    <div className="w-11 h-11 rounded-xl bg-[#FF451A]/10 flex items-center justify-center text-[#FF451A] shrink-0">
                      <Icon size={20} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-[#0F0F0F] dark:text-white text-base">
                        {tpl.name}
                      </div>
                      <p className="text-sm text-[#6B6B6B] dark:text-zinc-400 mt-0.5 leading-snug">
                        {tpl.description}
                      </p>
                      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 text-[11px] font-mono uppercase tracking-wider text-[#6B6B6B] dark:text-zinc-500">
                        <span>~{tpl.suggested_creator_count} creators</span>
                        <span>{tpl.suggested_duration_weeks}w duration</span>
                        <span>{tpl.platform === 'both' ? 'Instagram + YouTube' : tpl.platform}</span>
                      </div>
                    </div>
                    <Button
                      onClick={() => applyTemplate(tpl)}
                      className="bg-[#FF451A] text-white hover:bg-[#E63E17] rounded-full px-4 py-2 text-sm font-medium shrink-0"
                      data-testid={`use-template-${tpl.template_id}`}
                    >
                      Use Template
                    </Button>
                  </div>
                </div>
              );
            })}

            {!templatesLoading && (
              <button
                type="button"
                onClick={skipTemplates}
                className="w-full mt-2 border border-dashed border-black/[0.15] dark:border-white/15 rounded-2xl p-4 flex items-center justify-center gap-2 text-sm font-medium text-[#6B6B6B] dark:text-zinc-400 hover:border-[#FF451A]/40 hover:text-[#FF451A] transition-colors"
                data-testid="start-from-scratch-btn"
              >
                <FileText size={16} /> Start from scratch
              </button>
            )}
          </div>
        ) : (
          <>
        {selectedTemplateId && (
          <div className="mt-3 flex items-center gap-2">
            <button
              type="button"
              onClick={() => { setShowTemplates(true); setSelectedTemplateId(null); }}
              className="inline-flex items-center gap-1 text-xs font-medium text-[#6B6B6B] dark:text-zinc-400 hover:text-[#FF451A] transition-colors"
              data-testid="change-template-btn"
            >
              <ChevronLeft size={14} /> Change template
            </button>
            <span className="text-[11px] font-mono uppercase tracking-wider text-[#FF451A] bg-[#FF451A]/10 rounded-full px-2 py-0.5">
              {templates.find((t) => t.template_id === selectedTemplateId)?.name || selectedTemplateId}
            </span>
          </div>
        )}

        <div className="space-y-4 mt-4">
          {isAgency && (
            <div data-testid="agency-client-picker">
              <label className="text-xs font-semibold uppercase tracking-[0.08em] block mb-2 inline-flex items-center gap-1.5 text-[#0F0F0F] dark:text-white">
                <Building2 size={11} className="text-[#FF451A]" /> Client Brand *
              </label>
              {clientsLoading ? (
                <div className="text-xs text-[#6B6B6B] dark:text-zinc-400 py-2">Loading clients…</div>
              ) : agencyClients.length === 0 ? (
                <div className="text-xs text-[#FFB800] bg-[#FFB800]/10 border border-[#FFB800]/30 rounded-xl p-3" data-testid="agency-no-clients">
                  No client brands assigned. Ask your administrator to link brand accounts to your agency.
                </div>
              ) : (
                <Select value={clientBrandId} onValueChange={setClientBrandId}>
                  <SelectTrigger className="rounded-xl bg-white dark:bg-[#1A1D24] border border-black/[0.12] dark:border-white/[0.08] text-[#0F0F0F] dark:text-white placeholder-[#A1A1AA] dark:placeholder-zinc-500" data-testid="agency-client-select">
                    <SelectValue placeholder="Select the client this brief is for" />
                  </SelectTrigger>
                  <SelectContent>
                    {agencyClients.map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.name}{c.industry ? ` · ${c.industry}` : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
          )}

          <div>
            <label className="text-xs font-semibold uppercase tracking-[0.08em] block mb-2 text-[#0F0F0F] dark:text-white">Campaign Name *</label>
            <Input
              value={form.campaign_name}
              onChange={(e) => setForm({ ...form, campaign_name: e.target.value })}
              placeholder="Summer Beauty Collection 2026"
              className="rounded-xl bg-white dark:bg-[#1A1D24] border border-black/[0.12] dark:border-white/[0.08] text-[#0F0F0F] dark:text-white placeholder-[#A1A1AA] dark:placeholder-zinc-500"
              data-testid="brief-name-input"
            />
          </div>

          <div>
            <label className="text-xs font-semibold uppercase tracking-[0.08em] block mb-2 text-[#0F0F0F] dark:text-white">Campaign Brief *</label>
            <textarea
              value={form.brief}
              onChange={(e) => setForm({ ...form, brief: e.target.value })}
              placeholder="Describe your campaign goals, brand vibes, must-haves..."
              className="w-full rounded-xl px-4 py-3 min-h-[100px] bg-white dark:bg-[#1A1D24] border border-black/[0.12] dark:border-white/[0.08] text-[#0F0F0F] dark:text-white placeholder-[#A1A1AA] dark:placeholder-zinc-500"
              data-testid="brief-content-input"
            />
          </div>

          {/* Part 2 § 1 — Campaign Type. Determines routing once the brief lands. */}
          <div>
            <label className="text-xs font-semibold uppercase tracking-[0.08em] block mb-2 text-[#0F0F0F] dark:text-white">Campaign Type *</label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2" data-testid="brief-campaign-type">
              {[
                { v: 'exclusive', label: 'Exclusive Creator Campaign', sub: 'Best for 1–10 hand-picked creators' },
                { v: 'bulk',      label: 'Bulk Marketplace Campaign',  sub: 'Best for 50–500 creators, fixed cost' },
              ].map((opt) => (
                <button
                  key={opt.v}
                  type="button"
                  onClick={() => setForm({ ...form, campaign_type: opt.v })}
                  className={`text-left rounded-xl px-4 py-3 border transition-colors ${
                    form.campaign_type === opt.v
                      ? 'border-[#FF451A] bg-[#FF451A]/10'
                      : 'border-black/[0.10] dark:border-white/10 bg-white/5 hover:bg-white/10'
                  }`}
                  data-testid={`brief-campaign-type-${opt.v}`}
                >
                  <div className="text-sm font-semibold text-[#0F0F0F] dark:text-white">{opt.label}</div>
                  <div className="text-[11px] text-[#6B6B6B] dark:text-zinc-400">{opt.sub}</div>
                </button>
              ))}
            </div>
          </div>

          {form.campaign_type === 'bulk' && (
            <div>
              <label className="text-xs font-semibold uppercase tracking-[0.08em] block mb-2 text-[#0F0F0F] dark:text-white">Expected Creator Count</label>
              <div className="grid grid-cols-4 gap-2 mb-2" data-testid="brief-expected-presets">
                {[50, 100, 200, 500].map((n) => (
                  <button
                    key={n}
                    type="button"
                    onClick={() => setForm({ ...form, expected_creator_count: String(n) })}
                    className={`rounded-lg py-2 text-sm font-medium transition-colors ${
                      String(form.expected_creator_count) === String(n)
                        ? 'bg-[#FF451A] text-[#0F0F0F] dark:text-white'
                        : 'bg-white/5 border border-black/[0.10] dark:border-white/10 text-[#0F0F0F] dark:text-white hover:bg-white/10'
                    }`}
                    data-testid={`brief-expected-${n}`}
                  >
                    {n}
                  </button>
                ))}
              </div>
              <Input
                type="number"
                value={form.expected_creator_count}
                onChange={(e) => setForm({ ...form, expected_creator_count: e.target.value })}
                placeholder="Custom (e.g. 150)"
                className="rounded-xl bg-white dark:bg-[#1A1D24] border border-black/[0.12] dark:border-white/[0.08] text-[#0F0F0F] dark:text-white placeholder-[#A1A1AA] dark:placeholder-zinc-500"
                data-testid="brief-expected-count-input"
              />
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold uppercase tracking-[0.08em] block mb-2 text-[#0F0F0F] dark:text-white">Total Budget (₹) *</label>
              <Input
                type="number"
                value={form.budget}
                onChange={(e) => setForm({ ...form, budget: e.target.value })}
                placeholder="500000"
                className="rounded-xl bg-white dark:bg-[#1A1D24] border border-black/[0.12] dark:border-white/[0.08] text-[#0F0F0F] dark:text-white placeholder-[#A1A1AA] dark:placeholder-zinc-500"
                data-testid="brief-budget-input"
              />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-[0.08em] block mb-2 text-[#0F0F0F] dark:text-white">Creators Needed</label>
              <Input
                type="number"
                value={form.creator_count}
                onChange={(e) => setForm({ ...form, creator_count: e.target.value })}
                min="1"
                className="rounded-xl bg-white dark:bg-[#1A1D24] border border-black/[0.12] dark:border-white/[0.08] text-[#0F0F0F] dark:text-white placeholder-[#A1A1AA] dark:placeholder-zinc-500"
              />
            </div>
          </div>

          <div>
            <label className="text-xs font-semibold uppercase tracking-[0.08em] block mb-2 text-[#0F0F0F] dark:text-white">Platforms</label>
            <div className="flex flex-wrap gap-2">
              {PLATFORM_OPTIONS.map((p) => (
                <button
                  key={p}
                  type="button"
                  onClick={() => toggleArr('platforms', p)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium ${
                    form.platforms.includes(p) ? 'bg-[#FF451A] text-[#0F0F0F] dark:text-white' : 'bg-white dark:bg-[#0A0B0D] text-[#6B6B6B] dark:text-zinc-400 hover:bg-black/10'
                  }`}
                >
                  {p}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="text-xs font-semibold uppercase tracking-[0.08em] block mb-2 text-[#0F0F0F] dark:text-white">Deliverables</label>
            <div className="flex gap-2 mb-2">
              <Input
                value={deliverableInput}
                onChange={(e) => setDeliverableInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addDeliverable())}
                placeholder="e.g. 1 Reel + 3 Stories"
                className="rounded-xl bg-white dark:bg-[#1A1D24] border border-black/[0.12] dark:border-white/[0.08] text-[#0F0F0F] dark:text-white placeholder-[#A1A1AA] dark:placeholder-zinc-500"
              />
              <Button type="button" onClick={addDeliverable} className="bg-white dark:bg-[#0A0B0D] text-[#0F0F0F] dark:text-white rounded-xl px-4">Add</Button>
            </div>
            <div className="flex flex-wrap gap-2">
              {form.deliverables.map((d, i) => (
                <span key={i} className="px-3 py-1 rounded-full bg-[#0055FF]/10 text-[#0055FF] text-xs font-medium flex items-center gap-1">
                  {d}
                  <button onClick={() => setForm({ ...form, deliverables: form.deliverables.filter((_, idx) => idx !== i) })}>
                    <X size={12} />
                  </button>
                </span>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold uppercase tracking-[0.08em] block mb-2 text-[#0F0F0F] dark:text-white">Start Date</label>
              <Input
                type="date"
                value={form.timeline_start}
                onChange={(e) => setForm({ ...form, timeline_start: e.target.value })}
                className="rounded-xl bg-white dark:bg-[#1A1D24] border border-black/[0.12] dark:border-white/[0.08] text-[#0F0F0F] dark:text-white placeholder-[#A1A1AA] dark:placeholder-zinc-500"
              />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-[0.08em] block mb-2 text-[#0F0F0F] dark:text-white">End Date</label>
              <Input
                type="date"
                value={form.timeline_end}
                onChange={(e) => setForm({ ...form, timeline_end: e.target.value })}
                className="rounded-xl bg-white dark:bg-[#1A1D24] border border-black/[0.12] dark:border-white/[0.08] text-[#0F0F0F] dark:text-white placeholder-[#A1A1AA] dark:placeholder-zinc-500"
              />
            </div>
          </div>

          <div className="flex items-center gap-2 p-3 bg-white/[0.03] border border-black/[0.08] dark:border-white/5 rounded-xl">
            <Checkbox
              id="exclusive-only"
              checked={form.exclusive_only}
              onCheckedChange={(c) => setForm({ ...form, exclusive_only: !!c })}
              data-testid="exclusive-only-checkbox"
            />
            <label htmlFor="exclusive-only" className="text-sm cursor-pointer text-[#0F0F0F] dark:text-white">
              Only assign exclusive (managed) creators
            </label>
          </div>
        </div>

        <DialogFooter className="mt-6">
          <Button onClick={onClose} className="border border-black/[0.10] dark:border-white/10 bg-transparent text-[#0F0F0F] dark:text-white hover:bg-white/5 rounded-full px-6 py-2">
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={submitting}
            className="bg-[#FF451A] text-[#0F0F0F] dark:text-white rounded-full px-6 py-2 hover:bg-[#E63E17] disabled:opacity-50"
            data-testid="submit-brief-button"
          >
            {submitting ? 'Submitting...' : 'Submit Brief'}
          </Button>
        </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
